import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

export const storeApi = createApi({
  reducerPath: "storeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["KindredStore", "KindredStoreOptions", "StoreTemplateViewCounts", "ChallengeOverrides", "StoreFulfillment", "StoreNotifications", "MyOrders"],
  endpoints: (builder) => ({
    getKindredStore: builder.query({
      query: () => `/sales/portal/get-kindred-store`,
      providesTags: ["KindredStore"],
    }),
    getKindredStoreProduct: builder.query({
      query: (id) => `/sales/portal/get-kindred-store/${id}`,
      providesTags: ["KindredStore"],
    }),
    updateKindredStoreItem: builder.mutation({
      query: ({ recordId, fields }) => ({
        url: `/sales/portal/kindred-store-item/${recordId}`,
        method: "PATCH",
        body: { fields },
      }),
      invalidatesTags: ["KindredStore"],
    }),
    uploadKindredStoreItemImage: builder.mutation({
      query: ({ recordId, file }) => {
        const formData = new FormData();
        formData.append("image", file);
        return {
          url: `/sales/portal/kindred-store-item/${recordId}/image`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["KindredStore"],
    }),
    // Item Template endpoints (portal-store-redesign-spec.md §5.1, §5.4).
    // Drives the new Kindred Store — Admin storefront view.
    getStoreTemplates: builder.query({
      query: () => `/sales/portal/store/templates`,
      providesTags: ["KindredStore"],
    }),
    getStoreTemplateById: builder.query({
      query: (id) => `/sales/portal/store/templates/${id}`,
      providesTags: ["KindredStore"],
    }),
    // On-file delivery address for the checkout ship-to pre-fill (thread #1193).
    // The rep sees where the order would otherwise ship and can supply a
    // per-order override; BFF reads mirror-only, fail-soft.
    getStoreDeliveryAddress: builder.query({
      query: () => `/sales/portal/store/delivery-address`,
    }),
    // People who actually purchased a product — every Outfitters Store Orders
    // row across all of a template's variants. Backs the click on the green
    // cart badge in the admin grid (KindredStoreAdminList). List length matches
    // the badge's "Total Purchases" rollup.
    getStoreTemplatePurchasers: builder.query({
      query: (id) => `/sales/portal/store/templates/${id}/purchasers`,
      providesTags: ["KindredStore"],
    }),
    // §5.3 — PATCH a single template (Name / Description / Available Colors /
    // Available Sizes / Hero Photo / Incentive Type / Kindred Outfitters Item).
    // Backs the §5.5b.i template-level edit form.
    updateStoreTemplate: builder.mutation({
      query: ({ recordId, fields }) => ({
        url: `/sales/portal/store/templates/${recordId}`,
        method: "PATCH",
        body: { fields },
      }),
      invalidatesTags: ["KindredStore"],
    }),
    // §5.5a — REPLACES Hero Photo on Item Templates row. Retained for any
    // existing caller; the new admin gallery uses appendStoreTemplateHeroPhoto
    // (single upload that APPENDS) plus a regular updateStoreTemplate PATCH
    // to delete an attachment by filtering the array client-side.
    uploadStoreTemplateHeroPhoto: builder.mutation({
      query: ({ recordId, file }) => {
        const formData = new FormData();
        formData.append("image", file);
        return {
          url: `/sales/portal/store/templates/${recordId}/hero-photo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["KindredStore"],
    }),
    // APPENDS one image to the Hero Photo array. Backs the multi-image
    // gallery in the §5.5b.i admin TemplateEditForm — first image is the
    // storefront card thumbnail, additional ones rotate on rep press-and-hold.
    appendStoreTemplateHeroPhoto: builder.mutation({
      query: ({ recordId, file }) => {
        const formData = new FormData();
        formData.append("image", file);
        return {
          url: `/sales/portal/store/templates/${recordId}/hero-photos`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["KindredStore"],
    }),
    // §5.5a — REPLACES per-color Items.Photo (distinct from
    // uploadKindredStoreItemImage above, which APPENDS to Label/SDS for
    // historical chemical SDS sheets). Backs the §5.5b.ii.a variant grid's
    // per-row Replace Photo affordance.
    uploadKindredStoreItemPhoto: builder.mutation({
      query: ({ recordId, file }) => {
        const formData = new FormData();
        formData.append("image", file);
        return {
          url: `/sales/portal/kindred-store-item/${recordId}/photo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["KindredStore"],
    }),
    // §5.3 — POST a new variant under a template (mints Items row + HQ
    // Item Locations row). Backs the §5.5b.ii.c "+ Add Variant" header
    // dialog. 207 Partial means the Items row was created but the HQ row
    // failed — the dialog surfaces the orphan id so the admin can finish
    // it manually.
    createTemplateVariant: builder.mutation({
      query: ({ templateId, fields }) => ({
        url: `/sales/portal/store/templates/${templateId}/variants`,
        method: "POST",
        body: { fields },
      }),
      // Implicit option creation in §5.6.b can extend Items.Color/Size/etc. as
      // a side effect of this write — invalidate KindredStoreOptions so the
      // §5.6.c dropdowns refetch the universe immediately.
      invalidatesTags: ["KindredStore", "KindredStoreOptions"],
    }),
    // Bulk variant create — POST many variants under one template in a single
    // call (the Color×Size matrix in BulkVariantDialog). Body shape:
    // { variants: [{ fields }] }. 207 Partial means some rows
    // failed; data.results carries the per-row outcome (itemId / itemError /
    // itemLocationId / itemLocationError) so the dialog can report exactly
    // which squares didn't land. Same KindredStoreOptions invalidation as the
    // single create — implicit Color/Size/etc. option adds are a side effect.
    createTemplateVariantsBulk: builder.mutation({
      query: ({ templateId, variants }) => ({
        url: `/sales/portal/store/templates/${templateId}/variants/bulk`,
        method: "POST",
        body: { variants },
      }),
      invalidatesTags: ["KindredStore", "KindredStoreOptions"],
    }),
    // Per-color photo fan-out — upload ONE image and apply it as the Photo on
    // many variant (Items) rows at once (every size of a color from a bulk
    // create). One GCS upload, one batched Airtable write. 207 Partial means
    // some rows failed; data.results carries the per-row outcome. Backs the
    // per-color photo pickers in BulkVariantDialog.
    uploadKindredStoreItemsPhotoFanOut: builder.mutation({
      query: ({ file, recordIds }) => {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("recordIds", JSON.stringify(recordIds));
        return {
          url: `/sales/portal/store/items/photo-fanout`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["KindredStore"],
    }),
    // §5.3 — POST a new Item Template + first variant + HQ Item Locations row
    // in one call. Backs the "+ Add Template" header dialog on the admin list
    // (KindredStoreAdminList). Body shape mirrors the BFF createStoreTemplate
    // contract: { template: { fields }, variant: { fields } }.
    // 207 Partial means the template was created but the first variant or HQ
    // row failed — the dialog surfaces the orphan template id and navigates
    // into the detail view so the admin can finish there.
    createStoreTemplate: builder.mutation({
      query: ({ template, variant }) => ({
        url: `/sales/portal/store/templates`,
        method: "POST",
        body: { template, variant },
      }),
      // Same rationale as createTemplateVariant — implicit option creation
      // can extend Items + Item Templates option lists as a side effect.
      invalidatesTags: ["KindredStore", "KindredStoreOptions"],
    }),
    // Per-template aggregate view counts — drives the bottom-left popularity
    // badge on Kindred Store Admin cards. Returns [] until Josh applies the
    // portal_stats_view_count_store_template DDL (BFF fail-softs on
    // ER_NO_SUCH_TABLE), so the badge silently doesn't render until live.
    getStoreTemplateViewCounts: builder.query({
      query: () => `/sales/portal/store/templates/view-counts`,
      providesTags: ["StoreTemplateViewCounts"],
    }),
    // Fired by TemplateDetailView on mount when a portal user opens a
    // template in the rep storefront. Increments the aggregate + appends
    // an audit row. Aggregate badge invalidation is intentionally omitted —
    // the admin list refetches on its own cadence and we don't want every
    // rep view to trigger a redux refetch in someone else's session.
    trackStoreTemplateView: builder.mutation({
      query: (templateId) => ({
        url: `/sales/portal/store/templates/${templateId}/view`,
        method: "POST",
      }),
    }),
    // Phase 3 — per-rep Prize progress feed: "X of N" services + the shared
    // Count Down timer (start/end) for every Prize Item Template. Drives the
    // storefront thumbnail badge, the live countdown, and the expire/disappear
    // behavior. Loaded separately from the templates grid so it never blocks
    // it; on error/empty the storefront just renders prizes without a badge
    // (fail-soft, same posture as getStoreTemplateViewCounts).
    getRepPrizeProgress: builder.query({
      query: () => `/sales/portal/store/prize-progress`,
      providesTags: ["KindredStore"],
    }),
    // Phase 2 — per-rep prize-challenge store overrides: map of templateId →
    // { challengeId, title, details, pointsCost, expiresAt, mode }. The store
    // uses this to show the challenge price and earn-condition text on items
    // this rep can earn via a prize challenge. Fail-soft: on error the store
    // just shows normal pricing for those items.
    getChallengePrizeOverrides: builder.query({
      query: () => `/sales/portal/store/prize-challenge-overrides`,
      providesTags: ["KindredStore", "ChallengeOverrides"],
    }),
    // Submit/claim a store-prize challenge from within the Kindred Store detail
    // view. Mirrors the Balance page submitPointChallenge action but lives in the
    // store API slice so it can invalidate "ChallengeOverrides" and trigger a
    // refetch of the challenge banner. Files array may be empty for no-proof challenges.
    submitStorePrizeChallenge: builder.mutation({
      queryFn: async ({ challengeId, files = [] }, _api, _extraOptions, baseQuery) => {
        const form = new FormData();
        (files || []).forEach((f) => form.append("proof", f));
        return baseQuery({
          url: `/balance/portal/point-challenges/${challengeId}/submit`,
          method: "POST",
          body: form,
          // fetchBaseQuery strips Content-Type for FormData automatically.
          formData: true,
        });
      },
      invalidatesTags: ["ChallengeOverrides"],
    }),
    // Tournament round prize grants — per-rep map of unclaimed store-item prizes earned by
    // winning a tournament match. Keyed by templateRecId; merged alongside
    // challenge-prize-overrides in StoreTemplatesContent so the same badge
    // and override-price display handles both grant types.
    getTournamentRoundPrizeGrants: builder.query({
      query: () => `/sales/portal/store/tournament-round-prize-grants`,
      providesTags: ["KindredStore"],
    }),
    // Claim a single tournament prize grant (moves claimPts from the grant to a
    // store order in the BFF). Returns 200 on success, 409 if already claimed,
    // 403 if the grant belongs to a different rep.
    claimTournamentRoundPrizeGrant: builder.mutation({
      query: (grantId) => ({
        url: `/sales/portal/store/tournament-round-prize-grants/${grantId}/claim`,
        method: "POST",
      }),
      invalidatesTags: ["KindredStore"],
    }),
    // §5.6.a — current option lists for the four managed Items single-selects
    // (size / color / drop-ship-vendor / incentive-type). Used by §5.6.c
    // SelectWithAdd dropdowns and the Manage Option Lists sub-page.
    getStoreOptions: builder.query({
      query: () => `/sales/portal/store/options`,
      providesTags: ["KindredStoreOptions"],
    }),
    // §5.6.a — append option to a managed single-select. 200 = already
    // existed, 201 = newly added. Mirror-aware on the BFF side for
    // incentive-type (Items + Item Templates kept in sync).
    addStoreOption: builder.mutation({
      query: ({ slug, name }) => ({
        url: `/sales/portal/store/options/${slug}`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: ["KindredStoreOptions"],
    }),
    // §5.6.a — remove option. 409 with data.primaryTable.records[] when
    // still in use (UI surfaces the offenders so the admin can reassign).
    deleteStoreOption: builder.mutation({
      query: ({ slug, option }) => ({
        url: `/sales/portal/store/options/${slug}/${encodeURIComponent(option)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["KindredStoreOptions"],
    }),
    // ── Fulfillment admin (store admin only) ──────────────────────────────
    getAdminFulfillmentOrders: builder.query({
      query: ({ status = "all", days } = {}) => {
        const qs = new URLSearchParams({ status });
        if (days) qs.set("days", days);
        return `/sales/portal/store/admin/fulfillment?${qs}`;
      },
      providesTags: ["StoreFulfillment"],
    }),
    getAdminFulfillmentOrderDetail: builder.query({
      query: (taskRecid) => `/sales/portal/store/admin/fulfillment/${taskRecid}`,
      providesTags: ["StoreFulfillment"],
    }),
    patchAdminFulfillmentStatus: builder.mutation({
      query: ({ taskRecid, fulfillment_status, fulfillment_notes, estimated_delivery_date, delivery_notes }) => ({
        url: `/sales/portal/store/admin/fulfillment/${taskRecid}/status`,
        method: "PATCH",
        body: { fulfillment_status, fulfillment_notes, estimated_delivery_date, delivery_notes },
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Phase A: SMS notification templates (one per fulfillment status).
    getAdminSmsTemplates: builder.query({
      query: () => `/sales/portal/store/admin/sms-templates`,
      providesTags: ["StoreNotifications"],
    }),
    patchAdminSmsTemplate: builder.mutation({
      query: ({ status, message_template, enabled }) => ({
        url: `/sales/portal/store/admin/sms-templates/${encodeURIComponent(status)}`,
        method: "PATCH",
        body: { message_template, enabled },
      }),
      invalidatesTags: ["StoreNotifications"],
    }),
    // Phase A: Admin config (SOM phone, digest settings).
    getAdminStoreConfig: builder.query({
      query: () => `/sales/portal/store/admin/config`,
      providesTags: ["StoreNotifications"],
    }),
    patchAdminStoreConfig: builder.mutation({
      query: ({ key, value }) => ({
        url: `/sales/portal/store/admin/config`,
        method: "PATCH",
        body: { key, value },
      }),
      invalidatesTags: ["StoreNotifications"],
    }),
    // Phase B: Edit order inline (item swap, size, vendor, vendor_phone, cost).
    patchAdminFulfillmentOrder: builder.mutation({
      query: ({ taskRecid, ...fields }) => ({
        url: `/sales/portal/store/admin/fulfillment/${taskRecid}/order`,
        method: "PATCH",
        body: fields,
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Phase B: Out-of-stock resolution.
    postAdminFulfillmentOosResolve: builder.mutation({
      query: ({ taskRecid, action, replacement }) => ({
        url: `/sales/portal/store/admin/fulfillment/${taskRecid}/oos-resolve`,
        method: "POST",
        body: { action, replacement },
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Phase C: Status-change event log (immutable audit trail).
    getAdminFulfillmentEvents: builder.query({
      query: (taskRecid) => `/sales/portal/store/admin/fulfillment/${taskRecid}/events`,
      providesTags: ["StoreFulfillment"],
    }),
    // Phase C: Upload delivery proof photo (multipart FormData, field "photo").
    postAdminFulfillmentDeliveryPhoto: builder.mutation({
      queryFn: async ({ taskRecid, file }, _api, _extraOptions, baseQuery) => {
        const form = new FormData();
        form.append("photo", file);
        return baseQuery({
          url: `/sales/portal/store/admin/fulfillment/${taskRecid}/delivery-photo`,
          method: "POST",
          body: form,
          formData: true,
        });
      },
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Phase D: Rep-facing "My Orders" — the authenticated rep's own orders from MySQL.
    // Only shows delivery_notes (rep-visible), never internal fulfillment_notes.
    getMyFulfillmentOrders: builder.query({
      query: () => `/sales/portal/store/my-orders`,
      providesTags: ["MyOrders"],
    }),
    // Phase E: Bulk status advance — move many orders to the same status in one call.
    postAdminFulfillmentBulkStatus: builder.mutation({
      query: ({ taskRecids, newStatus }) => ({
        url: `/sales/portal/store/admin/fulfillment/bulk-status`,
        method: "POST",
        body: { taskRecids, newStatus },
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Phase E: Assign a tracking number + carrier to a single order.
    patchAdminFulfillmentTracking: builder.mutation({
      query: ({ taskRecid, tracking_number, carrier }) => ({
        url: `/sales/portal/store/admin/fulfillment/${taskRecid}/tracking`,
        method: "PATCH",
        body: { tracking_number, carrier },
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Phase E: Assign the same tracking number + carrier to many orders at once.
    postAdminFulfillmentBulkTracking: builder.mutation({
      query: ({ taskRecids, tracking_number, carrier }) => ({
        url: `/sales/portal/store/admin/fulfillment/bulk-tracking`,
        method: "POST",
        body: { taskRecids, tracking_number, carrier },
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Phase F Bug 7: Refresh an expired Airtable thumbnail URL for an admin fulfillment order.
    getAdminFulfillmentRefreshThumbnail: builder.query({
      query: (taskRecid) => `/sales/portal/store/admin/fulfillment/${taskRecid}/refresh-thumbnail`,
    }),
    // Phase F Bug 7: Rep-facing thumbnail refresh (ownership-gated).
    getRepFulfillmentRefreshThumbnail: builder.query({
      query: (taskRecid) => `/sales/portal/store/my-orders/${taskRecid}/refresh-thumbnail`,
    }),
    // Admin Cancel: mark order as Cancelled, trigger refund_pending + SMS.
    postAdminFulfillmentCancel: builder.mutation({
      query: (taskRecid) => ({
        url: `/sales/portal/store/admin/fulfillment/${taskRecid}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
    // Admin Swap Item: replace order item with a new store template item + swap SMS.
    postAdminFulfillmentSwap: builder.mutation({
      query: ({ taskRecid, ...body }) => ({
        url: `/sales/portal/store/admin/fulfillment/${taskRecid}/swap`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreFulfillment"],
    }),
  }),
});

export const {
  useGetKindredStoreQuery,
  useGetKindredStoreProductQuery,
  useUpdateKindredStoreItemMutation,
  useUploadKindredStoreItemImageMutation,
  useGetStoreTemplatesQuery,
  useGetStoreTemplateByIdQuery,
  useGetStoreDeliveryAddressQuery,
  useGetStoreTemplatePurchasersQuery,
  useUpdateStoreTemplateMutation,
  useUploadStoreTemplateHeroPhotoMutation,
  useAppendStoreTemplateHeroPhotoMutation,
  useUploadKindredStoreItemPhotoMutation,
  useCreateTemplateVariantMutation,
  useCreateTemplateVariantsBulkMutation,
  useUploadKindredStoreItemsPhotoFanOutMutation,
  useCreateStoreTemplateMutation,
  useGetStoreOptionsQuery,
  useAddStoreOptionMutation,
  useDeleteStoreOptionMutation,
  useGetStoreTemplateViewCountsQuery,
  useTrackStoreTemplateViewMutation,
  useGetRepPrizeProgressQuery,
  useGetChallengePrizeOverridesQuery,
  useGetTournamentRoundPrizeGrantsQuery,
  useClaimTournamentRoundPrizeGrantMutation,
  useSubmitStorePrizeChallengeMutation,
  useGetAdminFulfillmentOrdersQuery,
  useGetAdminFulfillmentOrderDetailQuery,
  usePatchAdminFulfillmentStatusMutation,
  useGetAdminSmsTemplatesQuery,
  usePatchAdminSmsTemplateMutation,
  useGetAdminStoreConfigQuery,
  usePatchAdminStoreConfigMutation,
  usePatchAdminFulfillmentOrderMutation,
  usePostAdminFulfillmentOosResolveMutation,
  useGetAdminFulfillmentEventsQuery,
  usePostAdminFulfillmentDeliveryPhotoMutation,
  useGetMyFulfillmentOrdersQuery,
  usePostAdminFulfillmentBulkStatusMutation,
  usePatchAdminFulfillmentTrackingMutation,
  usePostAdminFulfillmentBulkTrackingMutation,
  useGetAdminFulfillmentRefreshThumbnailQuery,
  useGetRepFulfillmentRefreshThumbnailQuery,
  usePostAdminFulfillmentCancelMutation,
  usePostAdminFulfillmentSwapMutation,
} = storeApi;
